/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 9: My profile, one grouped list like Gym settings. The URL is the
 * state (`?tab=`, alias `?section=`): live emails and the app link to
 * `profile?tab=memberships`. On a phone, no section means the list of sections.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconChevronRight } from "@tabler/icons-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
} from "@lemonadetraining/web-ui";
import {
  getUserInvites,
  subscribeToUserSubscriptions,
  useAppSelector,
} from "@lemonadetraining/web-shared";
import { isLiveSubscriptionStatus } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import type { Invite } from "@lemonadetraining/contracts";
import { useUserAccess } from "../../hooks/useUserAccess";
import ProfileInfo from "../../components/UserProfile/ProfileInfo";
import BodyDemographicsCard from "../../components/UserProfile/BodyDemographicsCard";
import ClassmateVisibilityCard from "../../components/UserProfile/ClassmateVisibilityCard";
import NotificationsTab from "../../components/UserProfile/NotificationsTab";
import AppearanceTab from "../../components/UserProfile/AppearanceTab";
import AccountSection from "../../components/UserProfile/AccountSection";
import MembershipsSection from "../../components/UserProfile/MembershipsSection";
import InvitationsSection from "../../components/UserProfile/InvitationsSection";
import type { SubscriptionData } from "../../components/UserProfile/ActiveSubscriptionItem";

export const RECORDS_PATH = "/dashboard/training?tab=records";

export const PROFILE_SECTIONS = [
  { id: "profile", label: "You", sub: "Name, photo, body and measurements" },
  { id: "memberships", label: "Memberships", sub: "Plans and visit cards" },
  { id: "invitations", label: "Invitations", sub: "Invitations for you and from you" },
  { id: "notifications", label: "Notifications", sub: "Which emails you get" },
  { id: "privacy", label: "Privacy", sub: "Show me to classmates" },
  { id: "appearance", label: "Appearance", sub: "Theme, time format and time zone" },
  { id: "account", label: "Account", sub: "Delete my account" },
] as const;
export type ProfileSection = (typeof PROFILE_SECTIONS)[number]["id"];

const isSection = (v: string | null): v is ProfileSection =>
  PROFILE_SECTIONS.some((s) => s.id === v);

type SubscriptionWithMirror = SubscriptionData & {
  commitment?: { dunningState?: string } | null;
};

const paymentNeedsYou = (subs: SubscriptionWithMirror[]) =>
  subs.some(
    (s) =>
      isLiveSubscriptionStatus(s.status) &&
      (s.status === "past_due" ||
        s.commitment?.dunningState === "grace" ||
        s.commitment?.dunningState === "paused")
  );

const ProfilePage: React.FC = () => {
  const access = useUserAccess();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab") ?? params.get("section");
  const chosen: ProfileSection | null = isSection(raw) ? raw : null;
  const section: ProfileSection = chosen ?? "profile";
  const userState = useAppSelector((state) => state.user);
  const uid = userState.user?.uid;

  const [subscriptions, setSubscriptions] = useState<SubscriptionWithMirror[] | null>(null);
  const [subscriptionsFailed, setSubscriptionsFailed] = useState(false);
  const [subscriptionsRetry, setSubscriptionsRetry] = useState(0);
  const [invites, setInvites] = useState<{ sent: Invite[]; received: Invite[] } | null>(null);
  const [invitesFailed, setInvitesFailed] = useState(false);

  // A listener: Cloud Functions write these after checkout, so a card that says
  // it is still being set up must resolve without a reload.
  useEffect(() => {
    if (!uid) return undefined;
    setSubscriptionsFailed(false);
    return subscribeToUserSubscriptions(
      uid,
      (subs: unknown) => setSubscriptions((subs as SubscriptionWithMirror[]) || []),
      () => setSubscriptionsFailed(true)
    );
  }, [uid, subscriptionsRetry]);

  const loadInvites = useCallback(async () => {
    if (!uid) return;
    setInvitesFailed(false);
    try {
      const all = await getUserInvites(uid);
      // Staff invitations live on Team (F032 D14).
      setInvites({
        sent: all.sent.filter((invite: Invite) => invite.invitationType !== "toBox"),
        received: all.received,
      });
    } catch (err) {
      console.error("Error loading invites:", err);
      setInvitesFailed(true);
    }
  }, [uid]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const go = (next: ProfileSection | null) => {
    const p = new URLSearchParams(params);
    p.delete("section");
    if (next) p.set("tab", next);
    else p.delete("tab");
    setParams(p);
  };

  const needs: Partial<Record<ProfileSection, string>> = {
    ...(subscriptions && paymentNeedsYou(subscriptions)
      ? { memberships: "A payment needs you" }
      : {}),
    ...(invites?.received.some((i) => i.status === "Pending")
      ? { invitations: "Waiting for you" }
      : {}),
  };
  const current = PROFILE_SECTIONS.find((s) => s.id === section)!;

  const content = (() => {
    switch (section) {
      case "memberships":
        return (
          <MembershipsSection
            subscriptions={subscriptions}
            failed={subscriptionsFailed}
            onRetry={() => setSubscriptionsRetry((n) => n + 1)}
          />
        );
      case "invitations":
        return (
          <InvitationsSection
            received={invites?.received ?? null}
            sent={invites?.sent ?? null}
            failed={invitesFailed}
            onReload={loadInvites}
            showTeamNote={access.isBoxAdmin}
          />
        );
      case "notifications":
        return <NotificationsTab />;
      case "privacy":
        return <ClassmateVisibilityCard />;
      case "appearance":
        return <AppearanceTab />;
      case "account":
        return <AccountSection onGoToMemberships={() => go("memberships")} />;
      default:
        return (
          <>
            <Card>
              <CardHeader>
                <CardTitle>You</CardTitle>
                <CardDescription>How you show up at your gym.</CardDescription>
              </CardHeader>
              <CardContent>
                <ProfileInfo
                  user={{
                    claims: (userState.claims ?? undefined) as { role?: string } | undefined,
                  }}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Body and demographics</CardTitle>
                <CardDescription>
                  Optional. Your gender and bodyweight tailor your fitness profile comparison. Leave
                  them blank to compare against a general adult baseline.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BodyDemographicsCard />
              </CardContent>
            </Card>
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3 text-sm"
              data-testid="profile-records-pointer"
            >
              <span className="min-w-0 flex-1">
                Looking for your PRs? They&apos;re now in{" "}
                <span className="font-medium">My training › Records</span>.
              </span>
              <Button asChild variant="outline" className="min-h-[44px]">
                <Link to={RECORDS_PATH}>Open Records</Link>
              </Button>
            </div>
          </>
        );
    }
  })();

  return (
    <div className="flex flex-col gap-5" data-testid="profile-page">
      <div className="grid items-start gap-5 md:grid-cols-[12rem_minmax(0,1fr)]">
        <nav aria-label="My profile sections" className="hidden flex-col gap-0.5 md:flex">
          {PROFILE_SECTIONS.map((s) => (
            <Button
              key={s.id}
              type="button"
              variant="ghost"
              aria-current={section === s.id ? "page" : undefined}
              onClick={() => go(s.id)}
              className={cn(
                "justify-start gap-2 font-normal text-muted-foreground",
                section === s.id && "bg-muted font-semibold text-foreground"
              )}
              data-testid={`profile-nav-${s.id}`}
            >
              {s.label}
              {needs[s.id] && (
                <>
                  <span className="ml-auto size-2 rounded-full bg-warning" aria-hidden="true" />
                  <span className="sr-only">, {needs[s.id]}</span>
                </>
              )}
            </Button>
          ))}
        </nav>

        {!chosen && (
          <nav
            aria-label="My profile sections"
            className="flex flex-col overflow-hidden rounded-xl border bg-card md:hidden"
            data-testid="profile-section-list"
          >
            {PROFILE_SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => go(s.id)}
                className="flex min-h-[52px] items-center gap-3 border-t px-4 py-3 text-left first:border-t-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                data-testid={`profile-list-${s.id}`}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm">{s.label}</span>
                  <span className="text-xs text-muted-foreground">{needs[s.id] ?? s.sub}</span>
                </span>
                {needs[s.id] && (
                  <span className="size-2 shrink-0 rounded-full bg-warning" aria-hidden="true" />
                )}
                <IconChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </button>
            ))}
          </nav>
        )}

        <div
          className={cn("min-w-0 flex-col gap-4", chosen ? "flex" : "hidden md:flex")}
          data-testid={`profile-section-${section}`}
        >
          {chosen && (
            <div className="flex flex-col items-start gap-1 md:hidden">
              <Button
                type="button"
                variant="link"
                className="min-h-[44px] px-0"
                onClick={() => go(null)}
                data-testid="profile-back"
              >
                ‹ My profile
              </Button>
              <h2 className="text-lg font-semibold">{current.label}</h2>
            </div>
          )}
          {content}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
